import React, { ReactElement, useMemo } from 'react';
import styled from '@emotion/styled';
import { getTextColor, getBackgroundTheme } from '../../../style';
import { areEqual, lazilyEvaluateVal, ternaryFunc } from '../../../utils/helpers';
import { renderAvatar } from '../../../common';
import OfferCard from '../../../atoms/OfferCard';
import OfferActionButton from './OfferActionButton';
import { vysionTrack } from '../../../utils/vysion/vysionService';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../constants/mixpanel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import breakpoints from '../../../theme/material/breakpoints';

const ResultItem = styled('div')(() => ({
  ...getTextColor().DARKER,
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: getBackgroundTheme().personalInfo,
  flexDirection: 'column',
  marginTop: 24,
  marginBottom: 24,
  position: 'relative',
  flexBasis: 'calc(25% - 15px)',
  padding: '0 10px',

  [breakpoints.up(700)]: {
    '&:nth-of-type(4n + 1)': {
      paddingLeft: 0,
    },
    '&:nth-of-type(4n)': {
      paddingRight: 0,
    },
  },
  [breakpoints.between(589, 700)]: {
    flexBasis: 'calc(33.33% - 15px)',
    '&:nth-of-type(3n + 1)': {
      paddingLeft: 0,
    },
    '&:nth-of-type(3n)': {
      paddingRight: 0,
    },
    [breakpoints.down(640)]: {
      flexBasis: 'calc(33.33% - 20px)',
      padding: '0 10px !important',
    },
  },
  [breakpoints.down(639)]: {
    margin: '10px 0px',
  },
  [breakpoints.down(590)]: {
    flexBasis: 'calc(50% - 20px)',
    padding: '0 10px !important',
  },
}));

interface OfferProfileGridViewProps {
  activeRoute: any;
  offerKey: any;
  profile: any;
  hashId: any;
  isFavoritedMe: any;
  isMatched: any;
  offer: any;
  pageTitle: any;
}

const OfferProfileGridView = ({
  activeRoute,
  offerKey,
  profile,
  hashId,
  isFavoritedMe,
  isMatched,
  offer,
  pageTitle,
}: OfferProfileGridViewProps): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const user = useSelector((state: RootState) => state.profile);

  const { trackProfileView, setMixpanelDataByKeyHandler } = useMixPanel();
  const profileData = profile.profile.data;
  const avatar = useMemo(
    () =>
      lazilyEvaluateVal(profileData, 3, false, 'avatar', 'data', 'urls') && renderAvatar(profileData.avatar.data.urls),
    [profileData?.avatar?.data?.urls]
  );
  const age = profileData.age;
  const city = profileData.city;
  const username = profile.username ? profile.username : '';
  const photoCount = parseInt(profileData.approved_public_photos_count);
  const privatePhotoCount = profileData.private_photos_count ? parseInt(profileData.private_photos_count) : 0;
  const online = profileData.online;
  const dataKey = `offer_${offerKey}`;
  const userVerified = profileData.id_verified;
  const isSelfieVerified = profileData.selfie_verified;

  const trackCardClickEvent = () => {
    vysionTrack({
      action: 'click',
      component: 'Profile Card',
      extraInfo: activeRoute === 'new' ? 'receivedOffers' : 'sentOffers',
      page: 'Offers',
    });

    trackProfileView({ Source: pageTitle, User: hashId });

    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.OFFER_MADE,
      payload: {
        source: user.isGenerous
          ? TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedRequest
          : TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedOffers,
      },
    });
  };

  return (
    <ResultItem className="css-offers-list-item grid-list-item">
      <OfferCard
        id={dataKey}
        age={age}
        city={city}
        username={username}
        hashId={hashId}
        photoCount={photoCount}
        privatePhotoCount={privatePhotoCount}
        online={online}
        imgUrl={avatar}
        cardWidth={ternaryFunc(isMobile, '100%', '100%')}
        cardMargin={ternaryFunc(isMobile, '5px 1%', '.5%')}
        detailsPadding={ternaryFunc(isMobile, '5px', '10px')}
        isVerified={userVerified}
        isSelfieVerified={isSelfieVerified}
        onClick={trackCardClickEvent}
        favoritedMe={isFavoritedMe}
        isMatched={isMatched}
      />
      <OfferActionButton
        offer={offer}
        otherAccount={profile}
        isMatched={isMatched}
        offerKey={offerKey}
        viewType="grid"
        isSuggestRequest={offer && offer.data && offer.data.suggest}
        pageTitle={pageTitle}
      />
    </ResultItem>
  );
};

export default React.memo(OfferProfileGridView, areEqual);
